/**
 * ServiceContainer - Dependency injection container for services
 *
 * Provides singleton instances of services based on environment and configuration.
 * This allows for easy mocking in development and testing environments.
 *
 * Pattern: Dependency Injection Container
 *
 * Usage:
 * ```typescript
 * import { serviceContainer } from './services/ServiceContainer';
 *
 * const playjectorService = serviceContainer.getPlayjectorService();
 * playjectorService.sendHostModeChanged(...);
 * ```
 */

import type { IPlayjectorService } from './playjector/interfaces/IPlayjectorService';
import { PlayjectorService } from './playjector/PlayjectorService';
import { MockPlayjectorService, setGlobalMockInstance } from './playjector/MockPlayjectorService';
import type { IRealtimeService } from './realtime/IRealtimeService';
import { RealRealtimeService } from './realtime/RealRealtimeService';
import { GameSaveStorageMessageService, GameSaveStorageRequestChannel, GameSaveStorageRealtimeChannel } from './gameSave/GameSaveStorageMessageService';
import type { ICampaignConfigService } from './campaign/ICampaignConfigService';
import { RealCampaignConfigService } from './campaign/RealCampaignConfigService';
import type { IPeerManager } from './peer/IPeerManager';
import { RealPeerManager } from './peer/RealPeerManager';
import type { IIpcService } from './ipc/IIpcService';
import { IpcService } from './ipc/IpcService';
import type { IElectronService } from './electron/IElectronService';
import { ElectronService } from './electron/ElectronService';
import type { IHostAnalyticsManager } from './analytics/IHostAnalyticsManager';
import { HostAnalyticsManager } from './analytics/hostAnalyticsManager';
import type { INonsoleReadinessService } from './nonsoleReadiness/INonsoleReadinessService';
import { NonsoleReadinessService } from './nonsoleReadiness/NonsoleReadinessService';
import { nonsoleReadinessState, hostModeEntryResultState } from '../api/stores/shared';
import { USE_MOCK_PLAYJECTOR, USE_MESSAGE_ROUTER, USE_PAVILION_COORDINATOR, USE_PLAYJECTOR_COORDINATOR } from '../config/featureFlags';
import type { RecordingControlManager as RecordingControlManagerType } from './playjector/managers/RecordingControlManager';
import { GameSaveManager, CampaignGameSaveConfigurationStore } from '../managers/game-save/GameSaveManager';
import { HostBaselineGameSaveSlotSelector } from '../managers/game-save/GameSaveSlotSelector';
import type { IGameSaveManager } from '../managers/game-save/IGameSaveManager';
import { GameSaveMessageService } from './gameSave/GameSaveMessageService';
import { GameSaveReplyChannel } from './gameSave/GameSaveReplyChannel';
import type { IGameSaveConfigurationAvailability } from './gameSave/IGameSaveConfigurationAvailability';
import { authState } from '../api/stores/shared';

/**
 * ServiceContainer - Singleton container for all services
 */
class ServiceContainer {
  private _playjectorService: IPlayjectorService | null = null;
  private _realtimeService: IRealtimeService | null = null;
  private _campaignConfigService: ICampaignConfigService | null = null;
  private _peerManager: IPeerManager | null = null;
  private _ipcService: IIpcService | null = null;
  private _electronService: IElectronService | null = null;
  private _analyticsManager: IHostAnalyticsManager | null = null;
  private _nonsoleReadinessService: INonsoleReadinessService | null = null;
  private _recordingControlManager: RecordingControlManagerType | null = null;
  private _gameSaveMessageService: GameSaveMessageService | null = null;
  private _gameSaveManager: IGameSaveManager | null = null;
  private _gameSaveStorageMessageService: GameSaveStorageMessageService | null = null;
  private _gameSaveStorageTransport: GameSaveStorageRealtimeChannel | null = null;
  readonly campaignGameSaveConfiguration = new CampaignGameSaveConfigurationStore();

  get gameSaveManager(): IGameSaveManager {
    if (!this._gameSaveManager) {
      this._gameSaveManager = new GameSaveManager({
        slotSelector: new HostBaselineGameSaveSlotSelector(), nativeExecutor: this.nativeGameSaveMessageService,
        configurationProvider: this.campaignGameSaveConfiguration,
      });
    }
    return this._gameSaveManager;
  }

  get gameSaveConfigurationAvailability(): IGameSaveConfigurationAvailability {
    return this.nativeGameSaveMessageService;
  }

  /** Pavilion coordinator initialization owns private storage listeners; Prime never installs them. */
  initializeGameSaveStorage(router: import('./MessageRouter').IMessageRouter): void {
    if (!USE_MESSAGE_ROUTER || !USE_PAVILION_COORDINATOR || !USE_PLAYJECTOR_COORDINATOR || this._gameSaveStorageMessageService) return;
    const transport = new GameSaveStorageRealtimeChannel(this.realtimeService);
    transport.registerMessageHandlers(router);
    this._gameSaveStorageTransport = transport;
    this._gameSaveStorageMessageService = new GameSaveStorageMessageService({
      requests: new GameSaveStorageRequestChannel(typeof window === 'undefined' ? undefined : window.playcast?.ipc),
      transport, ipcService: this.ipcService,
      hostIdentity: { getHostAuthId() { return authState.currentState.hostAuthId || 'host'; } },
    });
  }

  private get nativeGameSaveMessageService(): GameSaveMessageService {
    if (!this._gameSaveMessageService) {
      this._gameSaveMessageService = new GameSaveMessageService({
        configurationProvider: this.campaignGameSaveConfiguration,
        ipcService: this.ipcService,
        replyChannel: new GameSaveReplyChannel(typeof window === 'undefined' ? undefined : window.playcast?.ipc),
        hostIdentity: { getHostAuthId() { return authState.currentState.hostAuthId || 'host'; } },
      });
    }
    return this._gameSaveMessageService;
  }

  /**
   * Get the Realtime service instance (singleton)
   *
   * Always returns RealRealtimeService (no mock needed - MockRealtimeService is for tests only).
   */
  get realtimeService(): IRealtimeService {
    if (!this._realtimeService) {
      console.log('[ServiceContainer] 🌐 Creating RealRealtimeService');
      this._realtimeService = new RealRealtimeService();
    }

    return this._realtimeService;
  }

  /**
   * Get the Playjector service instance (singleton)
   *
   * Returns MockPlayjectorService or PlayjectorService based on USE_MOCK_PLAYJECTOR flag.
   */
  get playjectorService(): IPlayjectorService {
    if (!this._playjectorService) {
      if (USE_MOCK_PLAYJECTOR) {
        console.log('[ServiceContainer] 🎭 Using MockPlayjectorService (USE_MOCK_PLAYJECTOR = true)');
        const mockService = new MockPlayjectorService();
        this._playjectorService = mockService;

        // Expose to window for console access
        setGlobalMockInstance(mockService);

        if (typeof window !== 'undefined') {
          (window as unknown as { mockPlayjectorService: MockPlayjectorService }).mockPlayjectorService = mockService;
          console.log('[ServiceContainer] 💡 Access via: window.mockPlayjectorService or window.__mockPlayjector');
        }
      } else {
        console.log('[ServiceContainer] 🚀 Using PlayjectorService (USE_MOCK_PLAYJECTOR = false)');
        this._playjectorService = new PlayjectorService();
      }
    }

    return this._playjectorService;
  }

  /**
   * Get the Campaign Config service instance (singleton)
   *
   * Always returns RealCampaignConfigService (no mock needed - MockCampaignConfigService is for tests only).
   */
  get campaignConfigService(): ICampaignConfigService {
    if (!this._campaignConfigService) {
      console.log('[ServiceContainer] 📋 Creating RealCampaignConfigService');
      this._campaignConfigService = new RealCampaignConfigService();
    }

    return this._campaignConfigService;
  }

  /**
   * Get the Peer manager instance (singleton)
   *
   * Always returns RealPeerManager in production.
   * Tests can inject MockPeerManager by setting _peerManager before calling this getter.
   */
  get peerManager(): IPeerManager {
    if (!this._peerManager) {
      console.log('[ServiceContainer] 🔗 Creating RealPeerManager');
      this._peerManager = new RealPeerManager();
    }

    return this._peerManager;
  }

  /**
   * Get the IPC service instance (singleton)
   *
   * Provides IPC messaging to Electron/Playjector.
   */
  get ipcService(): IIpcService {
    if (!this._ipcService) {
      console.log('[ServiceContainer] 📤 Creating IpcService');
      this._ipcService = new IpcService();
    }

    return this._ipcService;
  }

  /**
   * Get the Electron service instance (singleton)
   *
   * Provides clipboard and host auth via the Electron preload bridge.
   */
  get electronService(): IElectronService {
    if (!this._electronService) {
      console.log('[ServiceContainer] ⚡ Creating ElectronService');
      this._electronService = new ElectronService();
    }

    return this._electronService;
  }

  /**
   * Get the analytics manager instance (singleton)
   *
   * Returns HostAnalyticsManager in production.
   * Tests can inject a mock by calling setAnalyticsManager() before accessing this getter.
   */
  get analyticsManager(): IHostAnalyticsManager {
    if (!this._analyticsManager) {
      console.log('[ServiceContainer] 📊 Creating HostAnalyticsManager');
      this._analyticsManager = new HostAnalyticsManager();
    }

    return this._analyticsManager!;
  }

  /**
   * Get the Nonsole Readiness service instance (singleton)
   *
   * Provides nonce-correlated IPC for fresh gate reports and host-mode entry requests.
   */
  get nonsoleReadinessService(): INonsoleReadinessService {
    if (!this._nonsoleReadinessService) {
      this._nonsoleReadinessService = new NonsoleReadinessService({
        ipcService: this.ipcService,
        reportStore: nonsoleReadinessState,
        entryStore: hostModeEntryResultState,
      });
    }

    return this._nonsoleReadinessService;
  }

  /**
   * Get the RecordingControlManager instance.
   *
   * Set externally from `coordinators/init.ts` after construction.
   * Throws if accessed before registration — upload-complete IPC replies
   * are only possible after a recording session, which requires the manager.
   */
  get recordingControlManager(): RecordingControlManagerType {
    if (!this._recordingControlManager) {
      throw new Error('RecordingControlManager not registered yet');
    }
    return this._recordingControlManager;
  }

  setRecordingControlManager(manager: RecordingControlManagerType): void {
    this._recordingControlManager = manager;
  }

  /**
   * Set the peer manager (for testing)
   */
  setPeerManager(peerManager: IPeerManager): void {
    this._peerManager = peerManager;
  }

  /**
   * Set the analytics manager (for testing)
   */
  setAnalyticsManager(analyticsManager: IHostAnalyticsManager): void {
    this._analyticsManager = analyticsManager;
  }

  /**
   * Reset all services (for testing)
   */
  reset(): void {
    this._gameSaveStorageMessageService?.destroy();
    this._gameSaveStorageMessageService = null;
    this._gameSaveStorageTransport?.destroy();
    this._gameSaveStorageTransport = null;
    this._gameSaveMessageService?.destroy();
    this._gameSaveMessageService = null;
    this._gameSaveManager = null;
    this._nonsoleReadinessService?.destroy();
    this._playjectorService = null;
    this._realtimeService = null;
    this._campaignConfigService = null;
    this._peerManager = null;
    this._ipcService = null;
    this._electronService = null;
    this._analyticsManager = null;
    this._nonsoleReadinessService = null;
    this._recordingControlManager = null;
  }
}

/**
 * Global singleton instance
 */
export const serviceContainer = new ServiceContainer();
